<template>
    <div class="pdf-viewer-container">
        <!-- Loading State -->
        <div v-if="loading" class="pdf-loading">
            <div class="spinner-container">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p class="mt-3">Loading document...</p>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="pdf-error">
            <i class="fas fa-exclamation-triangle fa-2x text-danger"></i>
            <p class="mt-3">{{ error }}</p>
            <b-button variant="outline-primary" size="sm" @click="loadPdf">
                <i class="fas fa-redo mr-1"></i>Retry
            </b-button>
        </div>

        <!-- PDF Pages -->
        <div v-else class="pdf-pages-wrapper" ref="pagesWrapper">
            <div
                v-for="pageNum in numPages"
                :key="pageNum"
                class="pdf-page-container"
            >
                <div class="page-number-badge">Page {{ pageNum }} of {{ numPages }}</div>
                <vue-pdf-embed
                    :source="pdfSource"
                    :page="pageNum"
                    class="pdf-page"
                    @rendered="onPageRendered(pageNum)"
                />
                <div v-if="viewedPages[pageNum]" class="page-viewed-indicator">
                    <i class="fas fa-check-circle"></i>
                </div>
                <!-- Sentinel at bottom of page to detect when user scrolls to bottom -->
                <div
                    :ref="`sentinel-${pageNum}`"
                    :data-page="pageNum"
                    class="page-sentinel"
                ></div>
            </div>
        </div>

        <!-- Progress Footer -->
        <div class="pdf-progress-footer" v-if="!loading && !error">
            <div class="progress-header">
                <div class="progress-info">
                    <span v-if="allPagesViewed" class="text-success">
                        <i class="fas fa-check-circle mr-1"></i>
                        Document reviewed
                    </span>
                    <span v-else class="text-muted">
                        <i class="fas fa-scroll mr-1"></i>
                        Scroll to review all pages
                    </span>
                </div>
                <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="downloadPdf"
                    class="download-btn"
                    title="Download PDF to view externally"
                >
                    <i class="fas fa-download mr-1"></i>
                    <span class="d-none d-sm-inline">Download &amp; Review</span>
                </b-button>
            </div>
            <b-progress
                :value="viewedPagesCount"
                :max="numPages"
                :variant="allPagesViewed ? 'success' : 'primary'"
                height="8px"
                class="mt-2"
            />
            <small class="text-muted mt-1 d-block">
                {{ viewedPagesCount }} of {{ numPages }} pages reviewed
            </small>
        </div>
    </div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';

export default {
    name: 'PdfDocumentViewer',
    components: {
        VuePdfEmbed
    },
    props: {
        pdfUrl: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: true,
            error: null,
            pdfSource: null,
            numPages: 0,
            viewedPages: {},  // Use object for Vue 2 reactivity
            observer: null,
            renderedPages: new Set()
        };
    },
    computed: {
        viewedPagesCount() {
            return Object.keys(this.viewedPages).length;
        },
        allPagesViewed() {
            return this.numPages > 0 && this.viewedPagesCount >= this.numPages;
        }
    },
    watch: {
        pdfUrl: {
            handler(newUrl) {
                if (newUrl) {
                    this.loadPdf();
                }
            },
            immediate: true
        },
        allPagesViewed(newVal) {
            if (newVal) {
                this.$emit('all-pages-viewed', {
                    totalPages: this.numPages
                });
                this.$emit('audit-event', {
                    eventType: 'pdf_all_pages_viewed',
                    data: { totalPages: this.numPages }
                });
            }
        }
    },
    mounted() {
        // Observer will be set up after PDF loads and wrapper is available
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        async loadPdf() {
            this.loading = true;
            this.error = null;
            this.viewedPages = {};  // Reset as empty object
            this.renderedPages = new Set();

            try {
                // Fetch the PDF as blob
                const response = await fetch(this.pdfUrl);

                if (!response.ok) {
                    throw new Error('Failed to load document');
                }

                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();

                // Get page count using pdfjs
                const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
                pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

                const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                this.numPages = pdf.numPages;

                // Create object URL for the PDF
                this.pdfSource = URL.createObjectURL(blob);

                this.$emit('audit-event', {
                    eventType: 'pdf_preview_loaded',
                    data: { totalPages: this.numPages }
                });

                // Set loading to false first so the wrapper renders
                this.loading = false;
                console.log('[PDF] PDF loaded, numPages:', this.numPages);

                // Set up intersection observer after DOM updates with wrapper available
                this.$nextTick(() => {
                    console.log('[PDF] $nextTick after load, setting up observer');
                    this.setupIntersectionObserver();
                });

            } catch (err) {
                console.error('Error loading PDF:', err);
                this.error = 'Unable to load the document. Please try again.';
                this.loading = false;
            }
        },

        setupIntersectionObserver() {
            // Get the scrollable wrapper element to use as root
            const wrapper = this.$refs.pagesWrapper;
            console.log('[PDF] Setting up intersection observer, wrapper:', !!wrapper);
            if (!wrapper) {
                console.warn('[PDF] Wrapper not available for intersection observer');
                return;
            }

            // Create observer to track when user scrolls to bottom of each page
            this.observer = new IntersectionObserver(
                (entries) => {
                    console.log('[PDF] Intersection callback fired, entries:', entries.length);
                    entries.forEach(entry => {
                        const pageNum = parseInt(entry.target.dataset.page, 10);

                        if (entry.isIntersecting && !this.viewedPages[pageNum]) {
                            // Mark page as viewed immediately when scrolled into view
                            console.log(`[PDF] Marking page ${pageNum} as viewed`);
                            this.$set(this.viewedPages, pageNum, true);

                            this.$emit('page-viewed', pageNum);
                            this.$emit('audit-event', {
                                eventType: 'pdf_page_viewed',
                                data: {
                                    page: pageNum,
                                    totalPages: this.numPages
                                }
                            });
                        }
                    });
                },
                {
                    root: wrapper, // Use the scroll container as root
                    threshold: 0, // Fire as soon as sentinel becomes visible
                    rootMargin: '0px'
                }
            );
            console.log('[PDF] Intersection observer created');
        },

        onPageRendered(pageNum) {
            this.renderedPages.add(pageNum);
            console.log(`[PDF] Page ${pageNum} rendered`);

            // Observe the sentinel at the bottom of this page
            this.$nextTick(() => {
                const sentinelRef = this.$refs[`sentinel-${pageNum}`];
                console.log(`[PDF] Setting up observer for page ${pageNum} sentinel:`, {
                    sentinelRef: !!sentinelRef,
                    sentinelElement: sentinelRef ? !!sentinelRef[0] : false,
                    observer: !!this.observer
                });
                if (sentinelRef && sentinelRef[0] && this.observer) {
                    this.observer.observe(sentinelRef[0]);
                    console.log(`[PDF] Now observing sentinel for page ${pageNum}`);
                } else {
                    console.warn(`[PDF] Could not observe sentinel for page ${pageNum} - missing ref or observer`);
                }
            });
        },

        downloadPdf() {
            if (!this.pdfSource) return;

            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = this.pdfSource;
            link.download = 'agreement-preview.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$emit('audit-event', {
                eventType: 'pdf_downloaded',
                data: { totalPages: this.numPages }
            });

            // Mark all pages as viewed — downloading implies intent to review externally
            if (!this.allPagesViewed) {
                for (let i = 1; i <= this.numPages; i++) {
                    if (!this.viewedPages[i]) {
                        this.$set(this.viewedPages, i, true);
                        this.$emit('page-viewed', i);
                    }
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.pdf-viewer-container {
    background: #f5f5f5;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
}

.pdf-loading,
.pdf-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px;
    text-align: center;
}

.pdf-pages-wrapper {
    max-height: 600px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y pinch-zoom;
    padding: 16px;
    background: #525659;

    @media (max-width: 576px) {
        max-height: 70vh;
        padding: 8px;
    }
}

.pdf-page-container {
    position: relative;
    margin-bottom: 16px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    overflow: hidden;

    &:last-child {
        margin-bottom: 0;
    }

    .page-sentinel {
        position: absolute;
        bottom: 200px;
        height: 1px;
        width: 100%;
        // Invisible trigger point near bottom of page
    }

    .page-number-badge {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.6);
        color: white;
        font-size: 11px;
        padding: 4px 8px;
        border-radius: 4px;
        z-index: 10;
    }

    .page-viewed-indicator {
        position: absolute;
        top: 8px;
        left: 8px;
        background: #28a745;
        color: white;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        z-index: 10;
    }
}

.pdf-page {
    width: 100%;
    display: block;

    ::v-deep canvas {
        width: 100% !important;
        height: auto !important;
    }
}

.pdf-progress-footer {
    padding: 16px;
    background: white;
    border-top: 1px solid #dee2e6;

    .progress-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
    }

    .progress-info {
        font-size: 14px;
        font-weight: 500;
    }

    .download-btn {
        flex-shrink: 0;
    }
}

// Mobile optimizations
@media (max-width: 576px) {
    .pdf-page-container {
        margin-bottom: 8px;
        border-radius: 2px;

        .page-number-badge {
            font-size: 10px;
            padding: 2px 6px;
        }

        .page-viewed-indicator {
            width: 20px;
            height: 20px;
            font-size: 10px;
        }
    }

    .pdf-progress-footer {
        padding: 12px;

        .progress-info {
            font-size: 13px;
        }
    }
}
</style>
